import { useEffect, useCallback } from "react";
import { LiveServerToolCall, FunctionResponse } from "@google/genai";
import { TodoItem, Project } from "../../../lib/adhd-todo-manager";
import { ADHDAPIAdapter } from "../../../lib/adhd-api-adapter";
import { useAPIClient } from "../../../lib/api-client";
import { timeService } from "../../../lib/time-service";
import { handleCreationError } from "../../../lib/error-handler";
import { logger } from "../../../lib/logger";
import { useSuccessToast, useErrorToast } from "../../toast-notification";
import { FunctionCallProcessor } from "../services/FunctionCallProcessor";

export interface ToolCallsHookProps {
  client: any; // The live API client
  adhdAdapter: ADHDAPIAdapter;
  userTimezone: string;
  addTodoOptimistically: (todo: TodoItem) => void;
  removeTodoOptimistically: (todoId: string) => void;
  updateTodosOptimistically: (todo: TodoItem) => void;
  addProjectOptimistically: (project: Project) => void;
  updateProjectOptimistically: (project: Project) => void;
  removeProjectOptimistically: (projectId: string) => void;
  setBodyDoublingSession: (session: any) => void;
  setTodos: (setter: (prev: TodoItem[]) => TodoItem[]) => void;
  setProjects: (setter: (prev: Project[]) => Project[]) => void;
  functionCallProcessor: FunctionCallProcessor | null;
  overwhelmAssessment: any;
  aiPersonalityMode: "supportive" | "focused" | "calm";
}

export const useToolCalls = ({
  client,
  adhdAdapter,
  userTimezone,
  addTodoOptimistically,
  removeTodoOptimistically,
  updateTodosOptimistically,
  addProjectOptimistically,
  updateProjectOptimistically,
  removeProjectOptimistically,
  setBodyDoublingSession,
  setTodos,
  setProjects,
  functionCallProcessor,
  overwhelmAssessment,
  aiPersonalityMode,
}: ToolCallsHookProps) => {
  const apiClient = useAPIClient();
  const showSuccess = useSuccessToast();
  const showError = useErrorToast();

  const processToolCall = useCallback(
    async (toolCall: LiveServerToolCall) => {
      if (!toolCall.functionCalls) return;

      const responses: FunctionResponse[] = [];
      const asyncOperations: Promise<void>[] = [];

      for (const fc of toolCall.functionCalls) {
        const processFunction = async () => {
          let response: any = { success: false };

          try {
            switch (fc.name) {
              case "create_todo":
                response = await handleCreateTodo(fc.args);
                break;

              case "create_project":
                response = await handleCreateProject(fc.args);
                break;

              case "update_project":
                response = await handleUpdateProject(fc.args);
                break;

              case "delete_project":
                response = await handleDeleteProject(fc.args);
                break;

              case "get_todos":
                response = await handleGetTodos(fc.args);
                break;

              case "get_projects":
                response = await handleGetProjects();
                break;

              case "update_todo":
                response = await handleUpdateTodo(fc.args);
                break;

              case "update_multiple_todos":
                response = await handleUpdateMultipleTodos(fc.args);
                break;

              case "delete_todo":
                response = await handleDeleteTodo(fc.args);
                break;

              case "get_focus_suggestions":
                response = await handleGetFocusSuggestions(fc.args);
                break;

              case "start_body_doubling":
                response = await handleStartBodyDoubling(fc.args);
                break;

              case "find_project_by_name":
                response = await handleFindProjectByName(fc.args);
                break;

              case "find_task_by_name":
                response = await handleFindTaskByName(fc.args);
                break;

              case "move_task_to_project":
                response = await handleMoveTaskToProject(fc.args);
                break;

              case "batch_move_tasks_to_project":
                response = await handleBatchMoveTasksToProject(fc.args);
                break;

              case "set_hyperfocus_break":
                response = await handleSetHyperfocusBreak(fc.args);
                break;

              case "suggest_project_assignments":
                response = await handleSuggestProjectAssignments(fc.args);
                break;

              case "apply_project_assignments":
                response = await handleApplyProjectAssignments(fc.args);
                break;

              case "reprioritize_all_tasks":
                response = await handleReprioritizeAllTasks();
                break;

              case "get_tasks_by_quadrant":
                response = await handleGetTasksByQuadrant(fc.args);
                break;

              case "get_prioritization_insights":
                response = await handleGetPrioritizationInsights();
                break;

              case "analyze_task_priority":
                response = await handleAnalyzeTaskPriority(fc.args);
                break;

              case "set_energy_context":
                response = await handleSetEnergyContext(fc.args);
                break;

              case "smart_project_assignment":
                response = await handleSmartProjectAssignment(fc.args);
                break;

              case "get_subscription_info":
                response = await handleGetSubscriptionInfo(fc.args);
                break;

              case "assign_todo_to_project":
                response = await handleAssignTodoToProject(fc.args);
                break;

              case "organize_todos_into_projects":
                response = await handleOrganizeTodosIntoProjects(fc.args);
                break;

              case "suggest_project_organization":
                response = await handleSuggestProjectOrganization(fc.args);
                break;

              // UI control functions that need special handling
              case "expand_project":
              case "collapse_project":
              case "expand_all_projects":
              case "collapse_all_projects":
              case "focus_project":
              case "focus_task":
                // These UI functions are handled by the parent component
                response = {
                  success: false,
                  message: "UI navigation functions are handled by the UI component",
                };
                break;

              default:
                logger.warn(`Unknown tool call: ${fc.name}`);
                response = { success: false, error: `Unknown function: ${fc.name}` };
            }
          } catch (error) {
            logger.error(`Error in tool call ${fc.name || "unknown"}:`, {
              component: "ToolCalls",
              action: fc.name || "unknown",
              error: error instanceof Error ? error.message : String(error),
            });
            response = {
              success: false,
              error: error instanceof Error ? error.message : String(error),
            };
          }

          // Generate ADHD-aware response
          const functionResponse: FunctionResponse = {
            name: fc.name || "unknown",
            response: { output: response },
            id: fc.id,
          };

          const enhancedResponse =
            functionCallProcessor?.generateADHDAwareResponse(
              functionResponse,
              fc.name || "unknown",
              response.success,
              overwhelmAssessment,
              aiPersonalityMode
            ) || functionResponse.response;

          responses.push({
            name: fc.name || "unknown",
            response: enhancedResponse,
            id: fc.id,
          });
        };

        asyncOperations.push(processFunction());
      }

      await Promise.all(asyncOperations);

      // Add setTimeout delay to match live-console pattern for better timing
      if (responses.length > 0) {
        setTimeout(() => client?.sendToolResponse({ functionResponses: responses }), 200);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      adhdAdapter,
      userTimezone,
      addTodoOptimistically,
      removeTodoOptimistically,
      updateTodosOptimistically,
      addProjectOptimistically,
      updateProjectOptimistically,
      removeProjectOptimistically,
      setBodyDoublingSession,
      setTodos,
      setProjects,
      apiClient,
      showSuccess,
      showError,
      functionCallProcessor,
      overwhelmAssessment,
      aiPersonalityMode,
    ]
  );

  // Individual handlers for each tool call type
  const handleCreateTodo = async (args: any) => {
    const todoArgs = { ...args };
    if (todoArgs.due_date && typeof todoArgs.due_date === "string") {
      todoArgs.due_date = timeService.parseUserDate(todoArgs.due_date, userTimezone);
    }
    if (todoArgs.scheduled_date && typeof todoArgs.scheduled_date === "string") {
      todoArgs.scheduled_date = timeService.parseUserDate(todoArgs.scheduled_date, userTimezone);
    }

    const useSmartPlacement = todoArgs.use_smart_placement !== false;

    if (useSmartPlacement && !todoArgs.project_id) {
      try {
        const smartResponse = await apiClient.post<any>(
          "/api/v1/intelligent-placement/create-todo",
          todoArgs
        );
        if (smartResponse.success && smartResponse.data?.todo) {
          addTodoOptimistically(smartResponse.data.todo);
          const todo = smartResponse.data.todo;
          const suggestion = smartResponse.data.suggestion;

          if (suggestion && suggestion.recommendedProjectId && suggestion.confidence >= 70) {
            showSuccess(
              "Smart Todo Created",
              `"${todo.title}" was intelligently assigned to project with ${suggestion.confidence}% confidence: ${suggestion.reasoning}`
            );
          } else {
            showSuccess("Todo Created", `"${todo.title}" has been added to your list.`);
          }

          return {
            success: true,
            todo: todo,
            suggestion: suggestion || null,
            message: suggestion
              ? `Task created and ${suggestion.recommendedProjectId ? "assigned to project" : "left unassigned"} with ${suggestion.confidence}% confidence. ${suggestion.reasoning}`
              : "Task created successfully",
          };
        }
      } catch (error) {
        if (
          error instanceof (window as any).SubscriptionLimitError ||
          (error as any).name === "SubscriptionLimitError"
        ) {
          handleCreationError(error, { entityType: "task", showError });

          return {
            success: false,
            error: "SUBSCRIPTION_LIMIT_EXCEEDED",
            limitType: (error as any).limitType || "todos",
            currentUsage: (error as any).currentUsage || 0,
            limit: (error as any).limit || 0,
            upgradeRequired: true,
            userFriendlyMessage:
              "You've reached your task creation limit for this billing period. Upgrade to create unlimited tasks and get access to AI-powered organization tools!",
          };
        }

        console.warn("Smart placement failed, falling back to regular creation:", error);
      }
    }

    // Regular creation or fallback
    try {
      const response = await adhdAdapter.createTodo(todoArgs);
      if (response.success && response.todo) {
        addTodoOptimistically(response.todo);
        showSuccess("Todo Created", `"${response.todo.title}" has been added to your list.`);
      }
      return response;
    } catch (error) {
      handleCreationError(error, { entityType: "task", showError });

      if (
        error instanceof (window as any).SubscriptionLimitError ||
        (error as any).name === "SubscriptionLimitError"
      ) {
        return {
          success: false,
          error: "SUBSCRIPTION_LIMIT_EXCEEDED",
          limitType: (error as any).limitType || "todos",
          currentUsage: (error as any).currentUsage || 0,
          limit: (error as any).limit || 0,
          upgradeRequired: true,
          userFriendlyMessage:
            "You've reached your task creation limit for this billing period. Upgrade to create unlimited tasks and get access to AI-powered organization tools!",
        };
      }

      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  };

  const handleCreateProject = async (args: any) => {
    try {
      const response = await adhdAdapter.createProject(args);
      if (response.success && response.project) {
        addProjectOptimistically(response.project);
        showSuccess("Project Created", `"${response.project.name}" project has been created.`);
      }
      return response;
    } catch (error) {
      handleCreationError(error, { entityType: "project", showError });

      if (
        error instanceof (window as any).SubscriptionLimitError ||
        (error as any).name === "SubscriptionLimitError"
      ) {
        return {
          success: false,
          error: "SUBSCRIPTION_LIMIT_EXCEEDED",
          limitType: (error as any).limitType || "projects",
          currentUsage: (error as any).currentUsage || 0,
          limit: (error as any).limit || 0,
          upgradeRequired: true,
          userFriendlyMessage:
            "You've reached your project creation limit for this billing period. Upgrade to create unlimited projects and unlock more ADHD-friendly features!",
        };
      }

      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  };

  const handleUpdateProject = async (args: any) => {
    const response = await adhdAdapter.updateProject(args);
    if (response.success && response.project) {
      updateProjectOptimistically(response.project);
    }
    return response;
  };

  const handleDeleteProject = async (args: any) => {
    const response = await adhdAdapter.deleteProject(args);
    if (response.success && args?.project_id) {
      const projectId = args.project_id;
      removeProjectOptimistically(projectId);
      // Also update todos that were in this project
      setTodos(prev =>
        prev.map(todo =>
          todo.project_id === projectId ? { ...todo, project_id: undefined } : todo
        )
      );
    }
    return response;
  };

  const handleGetTodos = async (args: any) => {
    const filteredTodos = await adhdAdapter.getFilteredTodos({
      filter_type: (args?.filter_type as any) || "all",
      project_id: args?.project_id as string,
      available_time_minutes: args?.available_time_minutes as number,
    });
    return {
      success: true,
      todos: filteredTodos,
      count: filteredTodos.length,
    };
  };

  const handleGetProjects = async () => {
    const allProjects = await adhdAdapter.getProjects();
    return {
      success: true,
      projects: allProjects,
      count: allProjects.length,
    };
  };

  const handleUpdateTodo = async (args: any) => {
    const updateArgs = { ...args };
    if (updateArgs.due_date && typeof updateArgs.due_date === "string") {
      updateArgs.due_date = timeService.parseUserDate(updateArgs.due_date, userTimezone);
    }
    if (updateArgs.scheduled_date && typeof updateArgs.scheduled_date === "string") {
      updateArgs.scheduled_date = timeService.parseUserDate(
        updateArgs.scheduled_date,
        userTimezone
      );
    }

    const response = await adhdAdapter.updateTodo(updateArgs);
    if (response.success && response.todo) {
      updateTodosOptimistically(response.todo);
      if (response.todo.status === "completed") {
        showSuccess("Todo Completed", `"${response.todo.title}" has been marked as completed! 🎉`);
      } else {
        showSuccess("Todo Updated", `"${response.todo.title}" has been updated.`);
      }
    }
    return response;
  };

  const handleUpdateMultipleTodos = async (args: any) => {
    const multipleUpdateArgs = { ...args };
    if (multipleUpdateArgs.updates && Array.isArray(multipleUpdateArgs.updates)) {
      multipleUpdateArgs.updates = multipleUpdateArgs.updates.map((update: any) => {
        const processedUpdate = { ...update };
        if (processedUpdate.due_date && typeof processedUpdate.due_date === "string") {
          processedUpdate.due_date = timeService.parseUserDate(
            processedUpdate.due_date,
            userTimezone
          );
        }
        if (processedUpdate.scheduled_date && typeof processedUpdate.scheduled_date === "string") {
          processedUpdate.scheduled_date = timeService.parseUserDate(
            processedUpdate.scheduled_date,
            userTimezone
          );
        }
        return processedUpdate;
      });
    }

    const response = await adhdAdapter.updateMultipleTodos(multipleUpdateArgs);
    if (response.success && response.updatedTodos) {
      response.updatedTodos.forEach(updateTodosOptimistically);
      logger.adhdAction("multiple_todos_updated", {
        count: response.updatedTodos.length,
        reason: args?.rebalance_reason || "Dynamic rebalancing",
      });
    }
    return response;
  };

  const handleDeleteTodo = async (args: any) => {
    const response = await adhdAdapter.deleteTodo(args);
    const todoIdToDelete = args?.todo_id;
    if (response.success && todoIdToDelete) {
      removeTodoOptimistically(todoIdToDelete as string);
    }
    return response;
  };

  const handleGetFocusSuggestions = async (args: any) => {
    const suggestions = await adhdAdapter.getFocusSuggestions(args || {});
    return {
      success: true,
      suggestions: suggestions.map(s => ({
        todo: s.todo,
        reason: s.reason,
        confidence: s.confidence,
      })),
    };
  };

  const handleStartBodyDoubling = async (args: any) => {
    const bodyDoublingResult = await adhdAdapter.startBodyDoubling({
      task_id: args?.task_id as string,
      duration_minutes: args?.duration_minutes as number,
      check_in_frequency: args?.check_in_frequency as number,
    });

    if (bodyDoublingResult.success && bodyDoublingResult.session) {
      const session = bodyDoublingResult.session;
      setBodyDoublingSession({
        taskId: session.taskId,
        startTime: session.startTime,
        duration: session.durationMinutes,
        checkInInterval: session.checkInIntervalMinutes,
      });

      showSuccess("Body Doubling Started", `Started focused session for "${session.task.title}"`);
      return {
        success: true,
        session: session,
        message: `Body doubling session started for "${session.task.title}" - ${session.durationMinutes} minutes with check-ins every ${session.checkInIntervalMinutes} minutes`,
      };
    }

    return bodyDoublingResult;
  };

  const handleFindProjectByName = async (args: any) => {
    const response = await adhdAdapter.findProjectByName({
      project_name: args?.project_name as string,
      exact_match: args?.exact_match as boolean,
    });

    if (response.success && response.projects) {
      const projects = response.projects;
      if (projects.length === 0) {
        return {
          success: true,
          projects: [],
          message: `No projects found matching "${args?.project_name}"`,
        };
      } else if (projects.length === 1) {
        return {
          success: true,
          projects: projects,
          message: `Found project: "${projects[0].name}" (ID: ${projects[0].id})`,
        };
      } else {
        return {
          success: true,
          projects: projects,
          message: `Found ${projects.length} projects matching "${args?.project_name}": ${projects.map(p => `"${p.name}"`).join(", ")}`,
        };
      }
    }

    return response;
  };

  const handleFindTaskByName = async (args: any) => {
    const response = await adhdAdapter.findTaskByName({
      task_name: args?.task_name as string,
      exact_match: args?.exact_match as boolean,
    });

    if (response.success && response.tasks) {
      const tasks = response.tasks;
      if (tasks.length === 0) {
        return {
          success: true,
          tasks: [],
          message: `No tasks found matching "${args?.task_name}"`,
        };
      } else if (tasks.length === 1) {
        return {
          success: true,
          tasks: tasks,
          message: `Found task: "${tasks[0].title}" (ID: ${tasks[0].id})`,
        };
      } else {
        return {
          success: true,
          tasks: tasks,
          message: `Found ${tasks.length} tasks matching "${args?.task_name}": ${tasks.map(t => `"${t.title}"`).join(", ")}`,
        };
      }
    }

    return response;
  };

  const handleMoveTaskToProject = async (args: any) => {
    const response = await adhdAdapter.moveTaskToProject({
      task_identifier: args?.task_identifier as string,
      project_identifier: args?.project_identifier as string,
    });

    if (response.success && response.task) {
      updateTodosOptimistically(response.task);
      const projectName = (response as any).projectName || "Unassigned";
      showSuccess("Task Moved", `"${response.task.title}" has been moved to ${projectName}`);
    }

    return response;
  };

  const handleBatchMoveTasksToProject = async (args: any) => {
    const response = await adhdAdapter.batchMoveTasksToProject({
      task_identifiers: args?.task_identifiers as string[],
      project_identifier: args?.project_identifier as string,
    });

    if (response.success && response.moved && response.moved.length > 0) {
      const refreshedTodos = await adhdAdapter.getTodos();
      setTodos(() => refreshedTodos);
      showSuccess("Batch Move Complete", response.message || "Tasks successfully moved");
    }

    return response;
  };

  const handleSetHyperfocusBreak = async (args: any) => {
    const response = await adhdAdapter.setHyperfocusBreak({
      break_interval: args?.break_interval as number,
      break_type: args?.break_type as any,
    });

    if (response.success) {
      showSuccess("Break Reminder Set", response.message || "Break reminders configured");
    }

    return response;
  };

  const handleSuggestProjectAssignments = async (args: any) => {
    try {
      const response = await apiClient.get("/api/v1/intelligent-placement/suggestions", {
        params: { limit: args?.limit || 10 },
      });

      if (response.success && response.data?.suggestions) {
        return {
          success: true,
          suggestions: response.data.suggestions,
          message: `Found ${response.data.suggestions.length} intelligent assignment suggestions`,
        };
      }

      return response;
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  };

  const handleApplyProjectAssignments = async (args: any) => {
    try {
      const assignments = args?.assignments || [];
      const response = await apiClient.post<any>("/api/v1/intelligent-placement/apply", {
        assignments: assignments,
      });

      if (response.success && response.data?.applied) {
        const refreshedTodos = await adhdAdapter.getTodos();
        setTodos(() => refreshedTodos);
        showSuccess(
          "Smart Assignments Applied",
          `Successfully organized ${response.data.applied.length} tasks into projects`
        );

        return {
          success: true,
          applied: response.data.applied,
          failed: response.data.failed || [],
          message: response.data.message,
        };
      }

      return response;
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  };

  const handleReprioritizeAllTasks = async () => {
    const response = await adhdAdapter.reprioritizeAllTodos();

    if (response.success && (response as any).updatedTodos) {
      (response as any).updatedTodos.forEach(updateTodosOptimistically);
      showSuccess(
        "Tasks Reprioritized",
        `${(response as any).updatedTodos.length} tasks have been reorganized using the Eisenhower Matrix`
      );
    }

    return response;
  };

  const handleGetTasksByQuadrant = async (args: any) => {
    const quadrant = args?.quadrant as 1 | 2 | 3 | 4;
    const includeContext = args?.include_adhd_context as boolean;

    const response = await adhdAdapter.getTasksByQuadrant(quadrant, includeContext);

    return {
      success: true,
      tasks: response,
      quadrant: quadrant,
      count: response.length,
      message: `Retrieved ${response.length} tasks from quadrant ${quadrant}`,
    };
  };

  const handleGetPrioritizationInsights = async () => {
    const insights = await adhdAdapter.getPrioritizationInsights();

    return {
      success: true,
      insights: insights,
      message: `Analyzed ${(insights as any).totalTasks || insights.quadrantDistribution ? Object.values(insights.quadrantDistribution).reduce((a: any, b: any) => a + b, 0) : 0} tasks for prioritization insights`,
    };
  };

  const handleAnalyzeTaskPriority = async (args: any) => {
    const taskId = args?.task_id as string;

    if (!taskId) {
      return { success: false, error: "Task ID is required" };
    }

    const todos = await adhdAdapter.getTodos();
    const task = todos.find(t => t.id === taskId);

    if (!task) {
      return { success: false, error: `Task with ID ${taskId} not found` };
    }

    // Get the Eisenhower Matrix from the todoManager via adapter
    const projects = await adhdAdapter.getProjects();
    const analysis = (adhdAdapter as any).todoManager.eisenhowerMatrix.analyzePriority(
      task,
      projects
    );

    return {
      success: true,
      task: task,
      analysis: analysis,
      message: `Priority analysis for "${task.title}": Quadrant ${analysis.eisenhowerQuadrant} (${analysis.confidence}% confidence)`,
    };
  };

  const handleSetEnergyContext = async (args: any) => {
    const energyLevel = args?.energy_level as "low" | "medium" | "high";
    const contextDescription = args?.context_description as string;
    const availableTime = args?.available_time_minutes as number;

    adhdAdapter.setEnergyContext({
      energy_level: energyLevel,
      context_description: contextDescription,
      available_time_minutes: availableTime,
    });

    return {
      success: true,
      energyLevel,
      context: contextDescription,
      availableTime,
      message: `Energy context updated: ${energyLevel} energy${contextDescription ? ` in ${contextDescription}` : ""}${availableTime ? ` with ${availableTime} minutes available` : ""}`,
    };
  };

  const handleSmartProjectAssignment = async (args: any) => {
    try {
      const taskIdentifiers = args?.task_identifiers as string[];
      const autoApply = (args?.auto_apply as boolean) || false;

      const suggestionsResponse = await apiClient.get("/api/v1/intelligent-placement/suggestions", {
        params: { task_identifiers: taskIdentifiers },
      });

      if (!suggestionsResponse.success || !suggestionsResponse.data?.suggestions) {
        return { success: false, error: "Could not generate suggestions" };
      }

      const suggestions = suggestionsResponse.data.suggestions;

      if (autoApply) {
        const highConfidenceSuggestions = suggestions.filter((s: any) => s.confidence >= 80);

        if (highConfidenceSuggestions.length > 0) {
          const applyResponse = await apiClient.post<any>("/api/v1/intelligent-placement/apply", {
            assignments: highConfidenceSuggestions.map((s: any) => ({
              todo_id: s.todoId,
              project_id: s.recommendedProjectId,
              confidence: s.confidence,
            })),
          });

          if (applyResponse.success && applyResponse.data?.applied) {
            const refreshedTodos = await adhdAdapter.getTodos();
            setTodos(() => refreshedTodos);
            showSuccess(
              "Smart Organization Complete",
              `Automatically organized ${applyResponse.data.applied.length} high-confidence tasks`
            );
          }
        }
      }

      return {
        success: true,
        suggestions: suggestions,
        autoApplied: autoApply ? suggestions.filter((s: any) => s.confidence >= 80).length : 0,
        message: `Generated ${suggestions.length} smart assignment suggestions`,
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  };

  const handleGetSubscriptionInfo = async (args: any) => {
    try {
      const includePricing = args?.include_pricing !== false;
      const includeUsage = args?.include_usage !== false;

      const [subscriptionResponse, usageResponse] = await Promise.all([
        apiClient.get<any>("/api/v1/subscriptions/current"),
        includeUsage ? apiClient.get<any>("/api/v1/subscriptions/usage") : Promise.resolve(null),
      ]);

      const result: any = {
        success: true,
        subscription: subscriptionResponse.data,
        message: `Current plan: ${subscriptionResponse.data?.plan || "Free"}`,
      };

      if (includeUsage && usageResponse) {
        result.usage = usageResponse.data;
      }

      if (includePricing) {
        result.pricing = {
          free: { price: 0, features: ["3 projects", "50 tasks", "Basic AI features"] },
          pro: {
            price: 9.99,
            features: ["Unlimited projects", "Unlimited tasks", "Advanced AI", "Priority support"],
          },
          team: {
            price: 29.99,
            features: ["Everything in Pro", "Team collaboration", "Analytics", "API access"],
          },
        };
      }

      return result;
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  };

  // Handler for assigning a single todo to a project
  const handleAssignTodoToProject = async (args: any) => {
    try {
      const { todo_id, project_id } = args;

      if (!todo_id || !project_id) {
        return {
          success: false,
          error: "Both todo_id and project_id are required",
        };
      }

      const result = await adhdAdapter.updateTodo({ todo_id, project_id });

      if (result.success && result.todo) {
        updateTodosOptimistically(result.todo);
        showSuccess("Task Organized", `Task has been assigned to project`);
        return {
          success: true,
          message: "Task successfully assigned to project",
        };
      }

      return result;
    } catch (error) {
      logger.error("Error assigning todo to project:", {
        component: "ToolCalls",
        action: "assignTodoToProject",
        error: error instanceof Error ? error.message : String(error),
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  };

  // Handler for batch organizing todos into projects
  const handleOrganizeTodosIntoProjects = async (args: any) => {
    try {
      const { assignments } = args;

      if (!assignments || !Array.isArray(assignments)) {
        return {
          success: false,
          error: "assignments array is required",
        };
      }

      const results = [];
      let successCount = 0;
      let failedCount = 0;

      for (const assignment of assignments) {
        try {
          const result = await adhdAdapter.updateTodo({
            todo_id: assignment.todo_id,
            project_id: assignment.project_id,
          });

          if (result.success && result.todo) {
            updateTodosOptimistically(result.todo);
            successCount++;
          } else {
            failedCount++;
          }

          results.push({
            todo_id: assignment.todo_id,
            success: result.success,
            error: result.error,
          });
        } catch (error) {
          failedCount++;
          results.push({
            todo_id: assignment.todo_id,
            success: false,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      if (successCount > 0) {
        showSuccess(
          "Tasks Organized",
          `Successfully organized ${successCount} task(s) into projects${failedCount > 0 ? ` (${failedCount} failed)` : ""}`
        );
      }

      return {
        success: successCount > 0,
        organized: successCount,
        failed: failedCount,
        total: assignments.length,
        results,
        message: `Organized ${successCount} of ${assignments.length} tasks`,
      };
    } catch (error) {
      logger.error("Error organizing todos:", {
        component: "ToolCalls",
        action: "organizeTodosIntoProjects",
        error: error instanceof Error ? error.message : String(error),
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  };

  // Handler for suggesting project organization
  const handleSuggestProjectOrganization = async (args: any) => {
    try {
      const { include_all_todos = false, suggest_new_projects = true } = args;

      // Get todos and projects
      const todos = await adhdAdapter.getTodos();
      const projects = await adhdAdapter.getProjects();

      if (!todos || !projects) {
        return {
          success: false,
          error: "Failed to fetch todos or projects",
        };
      }

      // Filter for unassigned todos unless include_all_todos is true
      const todosToOrganize = include_all_todos
        ? todos
        : todos.filter((t: TodoItem) => !t.project_id);

      if (todosToOrganize.length === 0) {
        return {
          success: true,
          message: "No unassigned tasks found to organize",
          suggestions: [],
        };
      }

      // Try to use intelligent placement API for suggestions
      try {
        const response = await apiClient.get("/api/v1/intelligent-placement/suggestions", {
          params: { limit: todosToOrganize.length },
        });

        if (response.success && response.data) {
          return {
            success: true,
            suggestions: response.data,
            available_projects: projects,
            unassigned_count: todosToOrganize.length,
            message: `Found ${todosToOrganize.length} task(s) that could be organized into ${projects.length} existing project(s)`,
          };
        }
      } catch (error) {
        logger.warn("Intelligent placement not available, using basic suggestions", {
          component: "ToolCalls",
          action: "suggestProjectOrganization",
        });
      }

      // Fallback to basic suggestions
      return {
        success: true,
        message: `Found ${todosToOrganize.length} unassigned task(s) and ${projects.length} project(s)`,
        unassigned_todos: todosToOrganize.map((t: TodoItem) => ({
          id: t.id,
          title: t.title,
          description: t.description,
        })),
        available_projects: projects.map((p: Project) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          task_count: todos.filter((t: TodoItem) => t.project_id === p.id).length,
        })),
        suggest_new_projects,
        recommendation:
          "Review the unassigned tasks and available projects to determine the best organization strategy",
      };
    } catch (error) {
      logger.error("Error suggesting project organization:", {
        component: "ToolCalls",
        action: "suggestProjectOrganization",
        error: error instanceof Error ? error.message : String(error),
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  };

  // Set up the tool call listener
  useEffect(() => {
    if (client) {
      client.on("toolcall", processToolCall);
      return () => client.off("toolcall", processToolCall);
    }
  }, [client, processToolCall]);
};
